import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  BookOpen, 
  Trophy, 
  Clock, 
  Target, 
  Brain,
  TrendingUp,
  Award,
  CheckCircle2,
  PlayCircle,
  RotateCcw,
  ChevronRight,
  Flame
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TestPrepEnhanced = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [tests, setTests] = useState([]);
  const [dailyQuestions, setDailyQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [myAttempts, setMyAttempts] = useState([]);
  const [stats, setStats] = useState({
    testsCompleted: 0,
    questionsAnswered: 0,
    accuracy: 0,
    streak: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchTests();
    fetchDailyQuestions();
    fetchMyAttempts();
  }, []);

  const fetchTests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/test-prep/tests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setTests(response.data.tests);
      }
    } catch (error) {
      console.error('Error fetching tests:', error);
    }
  };

  const fetchDailyQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get today's daily questions from the pre-generated batches
      const response = await axios.get(`${API}/test-prep/daily-questions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDailyQuestions(response.data.questions || []);
        
        if (response.data.questions.length === 0) {
          console.log('No daily questions available:', response.data.message);
          toast.info('Daily questions not yet generated. Please contact admin.');
        }
      }
    } catch (error) {
      console.error('Error fetching daily questions:', error);
      toast.error('Failed to load daily questions');
    }
  };

  const fetchMyAttempts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/test-prep/my-attempts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setMyAttempts(response.data.attempts);
        
        // Calculate stats
        const completed = response.data.attempts.filter(a => a.status === 'completed');
        const totalCorrect = completed.reduce((sum, a) => sum + a.score, 0);
        const totalQuestions = completed.length * 10; // Approximate
        
        setStats({
          testsCompleted: completed.length,
          questionsAnswered: totalQuestions,
          accuracy: totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0,
          streak: 0 // TODO: Calculate based on consecutive days
        });
      }
    } catch (error) {
      console.error('Error fetching attempts:', error);
    }
  };

  const startTest = (testId) => {
    navigate(`/test-taking/${testId}`);
  };

  // Group tests by exam type
  const testsByType = tests.reduce((acc, test) => {
    const type = test.exam_type || 'General';
    if (!acc[type]) acc[type] = [];
    acc[type].push(test);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            GRE & GMAT Prep
          </h1>
          <p className="text-gray-600">
            Practice mock exams and solve daily questions to ace your exams
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === 'overview'
                ? 'text-orange-600 border-b-2 border-orange-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('daily-questions')}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === 'daily-questions'
                ? 'text-orange-600 border-b-2 border-orange-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Daily Top 10
          </button>
          <button
            onClick={() => setActiveTab('mock-exams')}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === 'mock-exams'
                ? 'text-orange-600 border-b-2 border-orange-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Mock Exams
          </button>
          <button
            onClick={() => setActiveTab('my-progress')}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === 'my-progress'
                ? 'text-orange-600 border-b-2 border-orange-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            My Progress
          </button>
        </div>

        {/* Content Area */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Welcome Message */}
            <Card className="bg-gradient-to-r from-orange-50 to-blue-50 border-2 border-orange-200">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="h-12 w-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Trophy className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Welcome to GRE & GMAT Prep! 🎓
                    </h2>
                    <p className="text-gray-700">
                      Practice with thousands of GRE and GMAT questions. Start with Daily Top 10 for quick practice 
                      or take full-length mock exams to simulate the real test experience.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-2 border-blue-200">
                <CardContent className="pt-6 text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-1">{tests.length}</div>
                  <p className="text-gray-600">Available Tests</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-green-200">
                <CardContent className="pt-6 text-center">
                  <div className="text-3xl font-bold text-green-600 mb-1">{dailyQuestions.length}</div>
                  <p className="text-gray-600">Questions Today</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-orange-200">
                <CardContent className="pt-6 text-center">
                  <div className="text-3xl font-bold text-orange-600 mb-1">{stats.testsCompleted}</div>
                  <p className="text-gray-600">Tests Completed</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 border-blue-200 hover:border-blue-400 transition-all cursor-pointer"
                onClick={() => setActiveTab('daily-questions')}>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Target className="h-6 w-6 text-blue-600" />
                    <span>Daily Top 10 Questions</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    {dailyQuestions.length} fresh questions waiting for you today!
                  </p>
                  <Button className="w-full bg-blue-700 hover:bg-blue-700">
                    Start Daily Practice
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2 border-orange-200 hover:border-orange-400 transition-all cursor-pointer"
                onClick={() => setActiveTab('mock-exams')}>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <BookOpen className="h-6 w-6 text-orange-600" />
                    <span>Mock Exams</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    {tests.length} full-length practice tests available
                  </p>
                  <Button className="w-full bg-orange-600 hover:bg-orange-700">
                    Browse Mock Exams
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'daily-questions' && (
          <DailyQuestionsTab questions={dailyQuestions} onRefresh={fetchDailyQuestions} />
        )}

        {activeTab === 'mock-exams' && (
          <MockExamsTab tests={tests} testsByType={testsByType} onStartTest={startTest} />
        )}

        {activeTab === 'my-progress' && (
          <MyProgressTab attempts={myAttempts} stats={stats} />
        )}
      </div>
    </div>
  );
};

// Daily Questions Tab with Flip Cards
const DailyQuestionsTab = ({ questions, onRefresh }) => {
  const [flippedCards, setFlippedCards] = useState({});

  const handleCardFlip = (questionId) => {
    setFlippedCards(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  if (questions.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No Questions Available
          </h3>
          <p className="text-gray-600 mb-6">
            Questions will appear once tests are created by admin
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Daily Top 10 Questions</h2>
          <p className="text-sm sm:text-base text-gray-600">Tap any card to reveal the answer</p>
        </div>
        <Button onClick={onRefresh} variant="outline" className="w-full sm:w-auto">
          <RotateCcw className="h-4 w-4 mr-2" />
          Refresh Questions
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {questions.map((question, index) => (
          <QuestionCard
            key={question.id}
            question={question}
            index={index}
            isFlipped={flippedCards[question.id]}
            onFlip={() => handleCardFlip(question.id)}
          />
        ))}
      </div>
    </div>
  );
};

// Question Card Component with Flip Animation
const QuestionCard = ({ question, index, isFlipped, onFlip }) => {
  return (
    <div 
      className="relative cursor-pointer perspective-1000"
      style={{ 
        perspective: '1000px',
        minHeight: 'fit-content',
        height: 'auto'
      }}
    >
      <div 
        className={`relative w-full h-full transition-transform duration-500 transform-style-3d`}
        onClick={onFlip}
        onTouchEnd={onFlip}
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          minHeight: 'fit-content'
        }}
      >
        {/* Front of Card */}
        <Card className={`w-full h-auto backface-hidden border-2 ${
          question.difficulty === 'Easy' ? 'border-green-200' :
          question.difficulty === 'Hard' ? 'border-red-200' : 'border-blue-200'
        }`}
          style={{ 
            backfaceVisibility: 'hidden',
            position: isFlipped ? 'absolute' : 'relative',
            minHeight: 'fit-content'
          }}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <Badge variant="outline">Question {index + 1}</Badge>
              <Badge className={
                question.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                question.difficulty === 'Hard' ? 'bg-red-100 text-red-800' :
                'bg-blue-100 text-blue-800'
              }>
                {question.difficulty || 'Intermediate'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pb-6">
            <div className="space-y-4">
              <p className="text-gray-900 font-medium text-sm sm:text-base break-words whitespace-pre-wrap leading-relaxed">
                {question.question_text}
              </p>
              
              {question.options && question.options.length > 0 && (
                <div className="space-y-2">
                  {question.options.map((option) => (
                    <div key={option.id} className="flex items-start space-x-2 p-3 bg-gray-50 rounded-lg">
                      <span className="font-semibold text-gray-700 flex-shrink-0">{option.id}.</span>
                      <span className="text-gray-700 text-sm break-words flex-1">{option.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <p className="text-xs sm:text-sm text-gray-500 text-center mt-6 italic">
              Tap to see answer
            </p>
          </CardContent>
        </Card>

        {/* Back of Card */}
        <Card className={`w-full h-auto backface-hidden border-2 border-green-200 bg-green-50 ${isFlipped ? 'relative' : 'absolute'}`}
          style={{ 
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            minHeight: 'fit-content',
            top: 0,
            left: 0,
            right: 0
          }}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <Badge className="bg-green-600 text-white">Answer</Badge>
              <Badge variant="outline">Question {index + 1}</Badge>
            </div>
          </CardHeader>
          <CardContent className="pb-6">
            <div className="space-y-4">
              <div className="p-3 sm:p-4 bg-green-100 rounded-lg">
                <p className="text-xs sm:text-sm text-gray-600 mb-1">Correct Answer:</p>
                <p className="text-lg sm:text-xl font-bold text-green-800 break-words">
                  {question.correct_answer}
                </p>
              </div>
              
              <div>
                <p className="text-xs sm:text-sm text-gray-600 mb-2 font-semibold">Explanation:</p>
                <p className="text-gray-700 text-xs sm:text-sm break-words whitespace-pre-wrap leading-relaxed">
                  {question.explanation || 'No explanation available.'}
                </p>
              </div>
            </div>
            
            <p className="text-xs sm:text-sm text-gray-500 text-center mt-6 italic">
              Tap to flip back
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Mock Exams Tab
const MockExamsTab = ({ tests, testsByType, onStartTest }) => {
  if (tests.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No Mock Exams Available
          </h3>
          <p className="text-gray-600">
            Tests will appear once created by admin
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {Object.entries(testsByType).map(([examType, typeTests]) => (
        <div key={examType}>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <Award className="h-6 w-6 mr-2 text-orange-600" />
            {examType} Mock Exams
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {typeTests.map((test) => (
              <Card key={test.id} className="border-2 hover:border-orange-400 transition-all">
                <CardHeader>
                  <CardTitle className="text-lg">{test.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-4">{test.description}</p>
                  
                  <div className="flex items-center space-x-6 text-sm text-gray-600 mb-6">
                    <div className="flex items-center">
                      <BookOpen className="h-4 w-4 mr-1" />
                      {test.total_questions} Questions
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {test.duration_minutes} Minutes
                    </div>
                    <div className="flex items-center">
                      <Award className="h-4 w-4 mr-1" />
                      Pass: {test.passing_score}%
                    </div>
                  </div>

                  <Button 
                    onClick={() => onStartTest(test.id)}
                    className="w-full bg-orange-600 hover:bg-orange-700"
                  >
                    <PlayCircle className="h-4 w-4 mr-2" />
                    Start Test
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// My Progress Tab
const MyProgressTab = ({ attempts, stats }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">My Progress</h2>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tests Completed</p>
                <p className="text-2xl font-bold text-gray-900">{stats.testsCompleted}</p>
                <p className="text-xs text-gray-500 mt-1">Total attempts</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Questions Answered</p>
                <p className="text-2xl font-bold text-gray-900">{stats.questionsAnswered}</p>
                <p className="text-xs text-gray-500 mt-1">Across all tests</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <Target className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overall Accuracy</p>
                <p className="text-2xl font-bold text-gray-900">{stats.accuracy}%</p>
                <p className="text-xs text-gray-500 mt-1">Average score</p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Trophy className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Daily Streak</p>
                <p className="text-2xl font-bold text-gray-900">{stats.streak} 🔥</p>
                <p className="text-xs text-gray-500 mt-1">Consecutive days</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Flame className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Test History */}
      <Card>
        <CardHeader>
          <CardTitle>Test History</CardTitle>
        </CardHeader>
        <CardContent>
          {attempts.length === 0 ? (
            <div className="text-center py-12">
              <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No Tests Taken Yet
              </h3>
              <p className="text-gray-600">
                Start taking tests to see your progress here
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {attempts.filter(a => a.status === 'completed').map((attempt) => (
                <Card key={attempt.id} className="border-2">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">
                          Test Attempt
                        </h3>
                        <div className="flex items-center space-x-6 text-sm text-gray-600">
                          <div>Score: {attempt.score}/{attempt.total_questions || 'N/A'}</div>
                          <div>Percentage: {attempt.percentage}%</div>
                          <div>Time: {attempt.time_taken_minutes} min</div>
                          <Badge className={attempt.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {attempt.passed ? 'Passed' : 'Failed'}
                          </Badge>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Bottom spacing */}
      <div className="pb-24"></div>
    </div>
  );
};

export default TestPrepEnhanced;
